import { useEffect, useState } from "react";
import { ArrowDown, Bell, Cog, Database, Layers, Monitor } from "lucide-react";
import api from "../../lib/api";
import { architectureError, domainChips, domainLabel, plural, visibleDomains } from "../../lib/architecture";
import { LoadError } from "./ArchitectureTabs";
import { SourceRef } from "./ArchitectureBits";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../ui/sheet";
import { MobileSheet, MobileSheetBody, MobileSheetContent, MobileSheetHeader, MobileSheetTitle } from "../ui/mobile-sheet";

const Layer = ({ icon: Icon, title, hint, children, testid, tone = "slate" }) => (
  <section className={`rounded-xl border p-3 sm:p-4 ${tone === "teal" ? "border-teal-200 bg-teal-50/40" : tone === "indigo" ? "border-indigo-200 bg-indigo-50/40" : "border-slate-200 bg-white"}`} data-testid={testid}>
    <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900"><Icon size={16} className="text-slate-500" aria-hidden />{title}{hint ? <span className="text-xs font-normal text-slate-500">{hint}</span> : null}</h3>
    {children}
  </section>
);
const Link = ({ label }) => (
  <div className="flex items-center justify-center gap-2 py-1.5 text-xs text-slate-500" aria-hidden="true"><ArrowDown size={14} />{label}</div>
);
const Figure = ({ value, label }) => (<div className="min-w-0"><div className="text-lg font-semibold leading-tight text-slate-900">{Number(value).toLocaleString("fr-FR")}</div><div className="text-xs text-slate-600">{label}</div></div>);

// The map at a glance, layer by layer, built from the published registry (nothing drawn by hand): the interface, the domains of the server, then what
// they rest on. A click on a domain opens what it uses and what uses it; the full detail of an element is one step further.
export function OverviewTab({ snapshot, onPick }) {
  const [state, setState] = useState({ phase: "loading" });
  const [all, setAll] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setState({ phase: "loading" });
    api.get("/architecture/domains", { params: snapshot ? { snapshot } : {} })
      .then(({ data }) => live && setState({ phase: "ready", data }))
      .catch((e) => live && setState({ phase: "error", message: architectureError(e, "Impossible de charger la vue d'ensemble. Réessayez dans quelques instants.") }));
    return () => { live = false; };
  }, [snapshot, attempt]);

  if (state.phase === "loading") return <div className="space-y-3" data-testid="arch-loading"><Skeleton className="h-24 w-full" /><Skeleton className="h-56 w-full" /><Skeleton className="h-24 w-full" /></div>;
  if (state.phase === "error") return <LoadError message={state.message} onRetry={() => setAttempt((n) => n + 1)} />;
  const d = state.data;
  const shown = visibleDomains(d.modules, all);
  return (
    <div className="mx-auto max-w-6xl" data-testid="arch-overview">
      <Layer icon={Monitor} title="Interface React" hint="— application web du CRM" testid="layer-interface">
        <div className="flex flex-wrap gap-x-8 gap-y-2">
          <Figure value={d.interface.pages} label="pages" /><Figure value={d.interface.components} label="composants" /><Figure value={d.interface.api_calls} label="appels d'API" /><Figure value={d.interface.routes_reached} label="routes atteintes par les pages" />
        </div>
      </Layer>
      <Link label="HTTPS / JSON — chaque page appelle des routes de l'API" />
      <Layer icon={Layers} title="Domaines du serveur" hint={`— ${d.modules.length} domaines, les plus riches en routes d'abord`} tone="teal" testid="layer-domains">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {shown.map((m) => (
            <button key={m.id} type="button" onClick={() => onPick(m.id)} data-testid="overview-domain" data-id={m.id}
              className="flex min-w-0 flex-col gap-1 rounded-lg border border-slate-200 bg-white p-3 text-left hover:border-teal-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora">
              <span className="break-all text-sm font-semibold text-slate-900">{domainLabel(m.id)}</span>
              <span className="text-xs text-slate-600">{plural(m.endpoints, "route d'API", "routes d'API")}</span>
              <span className="flex flex-wrap gap-1">{domainChips(m).map((c) => (<span key={c} className="rounded bg-slate-100 px-1.5 py-0.5 text-2xs text-slate-700">{c}</span>))}</span>
            </button>
          ))}
        </div>
        {d.modules.length > shown.length || all ? (
          <Button variant="outline" size="sm" className="mt-3" onClick={() => setAll((v) => !v)} data-testid="overview-all">{all ? "Réduire" : `Voir les ${d.modules.length - shown.length} autres domaines`}</Button>
        ) : null}
      </Layer>
      <Link label="Lecture et écriture des données · tâches planifiées · événements" />
      <div className="grid gap-2 md:grid-cols-3">
        <Layer icon={Database} title="MongoDB" tone="indigo" testid="layer-data">
          <Figure value={d.data.collections} label="collections" />
          <p className="mt-1 text-xs text-slate-500">dont {d.data.transversal} traversées par presque toutes les requêtes (droits, journal, réglages) : non comptées dans les liens des domaines.</p>
        </Layer>
        <Layer icon={Cog} title="Tâches de fond" tone="indigo" testid="layer-workers">
          <Figure value={d.workers.tasks} label={`tâches, dans ${plural(d.workers.domains, "domaine", "domaines")}`} />
          <p className="mt-1 text-xs text-slate-500">Lancées par le serveur lui-même, pas par une requête.</p>
        </Layer>
        <Layer icon={Bell} title="Notifications" tone="indigo" testid="layer-notifications">
          <Figure value={d.notifications.events} label={`événements, dont ${d.notifications.with_template} avec message WhatsApp`} />
          <p className="mt-1 text-xs text-slate-500">Cloche et WhatsApp, avec le réglage et le sujet de chaque événement.</p>
        </Layer>
      </div>
    </div>
  );
}

function Weighted({ title, items, onPick, testid }) {
  if (!items.length) return null;
  return (
    <section className="py-3 border-b border-slate-100" data-testid={testid}>
      <h3 className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-slate-500">{title}</h3>
      <ul className="space-y-1">
        {items.map((item) => (
          <li key={item.id} className="flex items-center justify-between gap-2 text-sm">
            <button type="button" onClick={() => onPick(item.id)} className="min-w-0 break-all text-left font-medium text-jamora hover:underline [@media(pointer:coarse)]:min-h-11" data-testid="domain-link">{domainLabel(item.id)}</button>
            <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-2xs text-slate-600" title="Nombre d'appels de fonction entre les deux domaines">{plural(item.weight, "appel", "appels")}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ChipLinks({ title, names, prefix, onNode, testid }) {
  if (!names.length) return null;
  return (
    <section className="py-3 border-b border-slate-100" data-testid={testid}>
      <h3 className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-slate-500">{title}</h3>
      <div className="flex flex-wrap gap-1.5">
        {names.map((name) => (<button key={name} type="button" onClick={() => onNode(`${prefix}:${name}`)} className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-800 hover:border-jamora hover:text-jamora [@media(pointer:coarse)]:min-h-11" data-testid="domain-chip">{name}</button>))}
      </div>
    </section>
  );
}

// What one domain uses and what uses it (calls between domains, weighted), the data it writes and reads, the events it triggers, and a few of its files.
export function DomainPanel({ domainId, snapshot, mode, onPick, onNode, onBrowse, onClose }) {
  const [state, setState] = useState({ phase: "loading" });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setState({ phase: "loading" });
    api.get("/architecture/domain", { params: { id: domainId, ...(snapshot ? { snapshot } : {}) } })
      .then(({ data }) => live && setState({ phase: "ready", data }))
      .catch((e) => live && setState({ phase: "error", message: architectureError(e, "Impossible de charger ce domaine. Réessayez dans quelques instants.") }));
    return () => { live = false; };
  }, [domainId, snapshot, attempt]);
  const data = state.data;
  const header = (Tag, Title, drawer) => (
    <div className={`border-b border-slate-200 px-4 pb-3 pt-4 ${drawer ? "pr-12" : ""}`} data-testid="domain-header">
      <Title asChild><Tag tabIndex={-1} className="break-all text-base font-semibold text-slate-900 outline-none" data-testid="domain-title">{domainLabel(domainId)}</Tag></Title>
      <p className="mt-1 text-xs text-slate-600">{data ? `${plural(data.endpoints, "route d'API", "routes d'API")}${data.tasks ? ` · ${plural(data.tasks, "tâche de fond", "tâches de fond")}` : ""} · ${plural(data.pages_reaching, "route utilisée", "routes utilisées")} par l'interface` : "Domaine du serveur"}</p>
    </div>
  );
  const body = state.phase === "loading" ? <div className="space-y-3 p-4"><Skeleton className="h-16 w-full" /><Skeleton className="h-32 w-full" /></div>
    : state.phase === "error" ? (<div className="m-4"><LoadError message={state.message} onRetry={() => setAttempt((n) => n + 1)} /></div>)
      : (
        <div className="px-4 pb-6" data-testid="domain-body">
          <Weighted title="Ce domaine utilise" items={data.depends_on} onPick={onPick} testid="domain-depends" />
          <Weighted title="Est utilisé par" items={data.used_by} onPick={onPick} testid="domain-used-by" />
          {!data.depends_on.length && !data.used_by.length ? <p className="py-3 text-sm text-slate-600" data-testid="domain-alone">Aucun appel de fonction vers ou depuis un autre domaine n'a été trouvé (les appels par méthode d'objet ne sont pas suivis).</p> : null}
          <ChipLinks title="Collections écrites" names={data.collections.write} prefix="collection" onNode={onNode} testid="domain-writes" />
          <ChipLinks title="Collections seulement lues" names={data.collections.read} prefix="collection" onNode={onNode} testid="domain-reads" />
          <ChipLinks title="Événements déclenchés" names={data.events.map((e) => e.replace(/^event:/, ""))} prefix="event" onNode={onNode} testid="domain-events" />
          <section className="py-3" data-testid="domain-files">
            <h3 className="mb-1.5 flex items-center justify-between text-2xs font-semibold uppercase tracking-wider text-slate-500">Fichiers liés ({data.files.total})
              <button type="button" onClick={onBrowse} className="text-xs font-medium normal-case tracking-normal text-jamora hover:underline" data-testid="domain-browse">Voir tout le contenu</button></h3>
            <ul className="space-y-1.5">{data.files.items.map((f) => (<li key={f.id}><SourceRef path={f.path} line={null} /></li>))}</ul>
          </section>
        </div>
      );
  if (mode === "drawer") {
    return (
      <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
        <SheetContent side="right" className="flex w-[460px] max-w-full flex-col gap-0 p-0 sm:max-w-[460px]" data-testid="domain-panel" data-mode="drawer" aria-describedby={undefined}>
          <SheetDescription className="sr-only">Détail d'un domaine du serveur</SheetDescription>
          {header("h2", SheetTitle, true)}
          <div className="min-h-0 flex-1 overflow-y-auto">{body}</div>
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <MobileSheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <MobileSheetContent closeOnHandle data-testid="domain-panel" data-mode="sheet" aria-describedby={undefined}>
        <MobileSheetHeader className="block p-0">{header("h2", MobileSheetTitle, false)}</MobileSheetHeader>
        <MobileSheetBody className="p-0">{body}</MobileSheetBody>
      </MobileSheetContent>
    </MobileSheet>
  );
}
